import { Reference } from '@approvalmax/types';
import { DropdownEditor, FocusImplementer, NumberEditor } from '@approvalmax/ui';
import { intl } from '@approvalmax/utils';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import { ReadOnlyCell } from 'pages/requestForm/components';
import { ColumnDefinition } from 'pages/requestForm/selectors/types/ColumnDefinition';
import { getXeroContext } from 'pages/requestForm/selectors/xero';
import { useMemo } from 'react';

import { ExpandedXeroManualJournalLine } from '../../XeroManualJournalRequestForm.types';
import TextAreaEditorWrapper from '../TextAreaEditorWrapper/TextAreaEditorWrapper';
import { messages } from './ManualJournalLines.messages';
import { DropdownEditorContainer } from './ManualJournalLines.styles';
import { UseTableDefProps } from './ManualJournalLines.types';

const { xeroConstants } = constants;

export const useTableDef = (props: UseTableDefProps) => {
    const {
        lineAmountType,
        showErrors,
        showErrorsOnEmptyLine,
        readonlyFields,
        requiredFields,
        onLineFieldUpdate,
        onLineTrackingCategoryChange,
    } = props;

    const xeroContext = useSelector(getXeroContext);

    const { accounts, trackingCategories, taxCodes, emptyTaxCode } = xeroContext;

    const tableDef = useMemo<ColumnDefinition<ExpandedXeroManualJournalLine>[]>(
        () => [
            {
                id: 'number',
                name: messages.numberColName,
                fixedWidth: 60,
                cell: (li, options, lineItemIndex) => {
                    return <ReadOnlyCell text={String(lineItemIndex + 1)} />;
                },
            },
            {
                id: 'description',
                name: messages.descriptionColName,
                flexGrow: 2,
                readonly: () => false,
                invalid: () => showErrors && showErrorsOnEmptyLine,
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.description || ''} multiline />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <TextAreaEditorWrapper
                                    ref={registerFocusable}
                                    invalid={invalid}
                                    value={li.description}
                                    placeholder={messages.defaultInputPlaceholder}
                                    onLineFieldUpdate={onLineFieldUpdate}
                                    lineItemId={li.id}
                                />
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'account',
                name: messages.accountColName,
                flexGrow: 1,
                readonly: () => false,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (!li.isEmpty && !li.account) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.account?.text ?? ''} />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <DropdownEditorContainer>
                                    <PlainDataProvider items={accounts}>
                                        <DropdownEditor
                                            ref={registerFocusable}
                                            invalid={invalid}
                                            value={li.account || null}
                                            singleLinePerItem={false}
                                            onChange={(value: Reference | null) => {
                                                onLineFieldUpdate(li.id, 'account', value ?? undefined);
                                            }}
                                            placeholder={messages.defaultSelectPlaceholder}
                                            theme='transparent'
                                        />
                                    </PlainDataProvider>
                                </DropdownEditorContainer>
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'taxRate',
                name: messages.taxRateColName,
                flexGrow: 1,
                readonly: () => lineAmountType === domain.LineAmountType.NoTax,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (lineAmountType !== domain.LineAmountType.NoTax && !li.tax && !li.isEmpty) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) => {
                    const tax =
                        lineAmountType === domain.LineAmountType.NoTax
                            ? li.isEmpty
                                ? undefined
                                : emptyTaxCode
                            : li.tax;

                    const accountClass = li.account && (li.account as domain.XeroAccount).accountClass;

                    const taxCodesList = accountClass
                        ? taxCodes.filter((t) => t.applicableToAccountClasses.includes(accountClass))
                        : taxCodes;

                    return (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <PlainDataProvider items={taxCodesList}>
                                    <DropdownEditor
                                        ref={registerFocusable}
                                        value={tax || null}
                                        disabled={readonly}
                                        invalid={invalid}
                                        onChange={(newTax: domain.XeroTaxCode | null) =>
                                            onLineFieldUpdate(li.id, 'tax', newTax ?? undefined)
                                        }
                                        placeholder={messages.defaultSelectPlaceholder}
                                        theme='transparent'
                                    />
                                </PlainDataProvider>
                            )}
                        </FocusImplementer>
                    );
                },
            },
            {
                id: 'taxAmount',
                name: messages.taxAmountColName,
                alignRight: true,
                flexGrow: 1,
                readonly: () => true,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (
                        !li.isEmpty &&
                        li.tax &&
                        lineAmountType !== domain.LineAmountType.NoTax &&
                        typeof li.taxAmount !== 'number'
                    ) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { readonly, invalid }) => {
                    return readonly ? (
                        <ReadOnlyCell text={li.isEmpty ? '' : intl.formatNumber(li.taxAmount ?? 0, 'auto')} />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <>
                                    <NumberEditor
                                        ref={registerFocusable}
                                        allowFloat
                                        precision={2}
                                        invalid={invalid}
                                        value={li.taxAmount ?? null}
                                        onChange={(newValue: number | null) => {
                                            onLineFieldUpdate(li.id, 'taxAmount', newValue ?? undefined);
                                        }}
                                        placeholder={messages.defaultInputPlaceholder}
                                        theme='transparent'
                                        align='right'
                                        displayFormat='currency-auto'
                                        changeOnBlur={false}
                                    />
                                </>
                            )}
                        </FocusImplementer>
                    );
                },
            },
            ...trackingCategories.map(
                (trackingCategory): ColumnDefinition<ExpandedXeroManualJournalLine> => ({
                    id: trackingCategory.category.id,
                    name: trackingCategory.category.text,
                    flexGrow: 1,
                    readonly: () => readonlyFields?.includes(trackingCategory.category.id),
                    invalid: (li) => {
                        if (!showErrors) {
                            return false;
                        }

                        const trackingItem = li.tracking.find(
                            (item) => item.category.id === trackingCategory.category.id
                        );

                        if (
                            requiredFields.includes(trackingCategory.category.id) &&
                            !trackingItem?.value &&
                            !li.isEmpty
                        ) {
                            return true;
                        }

                        return showErrorsOnEmptyLine;
                    },
                    cell: (li, { readonly, invalid }) => {
                        const trackingItem = li.tracking.find(
                            (item) => item.category.id === trackingCategory.category.id
                        );

                        return readonly ? (
                            <ReadOnlyCell text={trackingItem && trackingItem.value && trackingItem.value.text} />
                        ) : (
                            <FocusImplementer>
                                {({ registerFocusable }) => (
                                    <PlainDataProvider items={trackingCategory.options}>
                                        <DropdownEditor
                                            ref={registerFocusable}
                                            invalid={invalid}
                                            value={trackingItem ? trackingItem.value : null}
                                            singleLinePerItem={false}
                                            onChange={(newValue: Reference | null) =>
                                                onLineTrackingCategoryChange(
                                                    li.id,
                                                    trackingCategory.category.id,
                                                    newValue
                                                )
                                            }
                                            placeholder={messages.defaultSelectPlaceholder}
                                            theme='transparent'
                                        />
                                    </PlainDataProvider>
                                )}
                            </FocusImplementer>
                        );
                    },
                })
            ),
            {
                id: 'debit',
                name: messages.debitColName,
                alignRight: true,
                flexGrow: 1,
                readonly: () => false,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (!li.isEmpty && typeof li.amount !== 'number') {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell
                            text={
                                typeof li.amount === 'number' &&
                                li.amountType === domain.XeroManualJournalLineAmountType.Debit
                                    ? intl.formatNumber(li.amount, 'auto')
                                    : ''
                            }
                        />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <>
                                    <NumberEditor
                                        ref={registerFocusable}
                                        allowFloat
                                        invalid={invalid}
                                        value={
                                            typeof li.amount === 'number' &&
                                            li.amountType === domain.XeroManualJournalLineAmountType.Debit
                                                ? li.amount
                                                : null
                                        }
                                        onChange={(newValue: number | null) => {
                                            const isNegativeValue = newValue !== null && newValue < 0;

                                            const newAmount = isNegativeValue ? newValue * -1 : newValue;

                                            onLineFieldUpdate(li.id, 'amount', newAmount ?? undefined);
                                            onLineFieldUpdate(
                                                li.id,
                                                'amountType',
                                                isNegativeValue
                                                    ? domain.XeroManualJournalLineAmountType.Credit
                                                    : domain.XeroManualJournalLineAmountType.Debit
                                            );
                                        }}
                                        placeholder={messages.defaultInputPlaceholder}
                                        theme='transparent'
                                        align='right'
                                        displayFormat='currency-auto'
                                        max={xeroConstants.MAX_AMOUNT}
                                        precision={2}
                                        allowCalculation
                                    />
                                </>
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'credit',
                name: messages.creditColName,
                alignRight: true,
                flexGrow: 1,
                readonly: () => false,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (!li.isEmpty && typeof li.amount !== 'number') {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell
                            text={
                                typeof li.amount === 'number' &&
                                li.amountType === domain.XeroManualJournalLineAmountType.Credit
                                    ? intl.formatNumber(li.amount, 'auto')
                                    : ''
                            }
                        />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <>
                                    <NumberEditor
                                        ref={registerFocusable}
                                        allowFloat
                                        invalid={invalid}
                                        value={
                                            typeof li.amount === 'number' &&
                                            li.amountType === domain.XeroManualJournalLineAmountType.Credit
                                                ? li.amount
                                                : null
                                        }
                                        onChange={(newValue: number | null) => {
                                            const isNegativeValue = newValue !== null && newValue < 0;

                                            const newAmount = isNegativeValue ? newValue * -1 : newValue;

                                            onLineFieldUpdate(li.id, 'amount', newAmount ?? undefined);
                                            onLineFieldUpdate(
                                                li.id,
                                                'amountType',
                                                isNegativeValue
                                                    ? domain.XeroManualJournalLineAmountType.Debit
                                                    : domain.XeroManualJournalLineAmountType.Credit
                                            );
                                        }}
                                        placeholder={messages.defaultInputPlaceholder}
                                        theme='transparent'
                                        align='right'
                                        displayFormat='currency-auto'
                                        max={xeroConstants.MAX_AMOUNT}
                                        precision={2}
                                        allowCalculation
                                    />
                                </>
                            )}
                        </FocusImplementer>
                    ),
            },
        ],
        [
            accounts,
            showErrors,
            showErrorsOnEmptyLine,
            readonlyFields,
            requiredFields,
            trackingCategories,
            lineAmountType,
            taxCodes,
            emptyTaxCode,
            onLineFieldUpdate,
            onLineTrackingCategoryChange,
        ]
    );

    return tableDef;
};
